/**
 * The guide's avatar: a teddy bear drawn inline as SVG.
 *
 * Inline rather than an image file because the widget lives in a shadow root on
 * a shop whose content-security policy, lazy-loading plugin or CDN rewrite may
 * refuse an external picture — and because the parts have to move: the head
 * tilts toward what the bear is talking about, the eyes blink, and the right
 * arm rotates about its shoulder to point at the layer the shopper is changing.
 *
 * Geometry is in a 120×120 box. The moving parts and their pivots:
 *   .pzg-head  — pivots at (60, 80), the neck; `--pzg-tilt` degrees
 *   .pzg-arm   — pivots at (84, 88), the shoulder; `--pzg-aim` degrees, where
 *                0° points right, negative points up. The bear is mirrored as a
 *                whole (`.is-flipped`) when the target is on its left.
 *   .pzg-eyes  — blink by scaling about (60, 48)
 *   .pzg-bear  — breathes (a 1–2 px bob) and bounces when happy
 * The scarf takes the shop's accent colour so the bear belongs to the theme.
 */
export const GUIDE_AVATAR_SVG = `<svg viewBox="0 0 120 120" aria-hidden="true" focusable="false">
  <defs>
    <radialGradient id="pzg-fur" cx="42%" cy="34%" r="72%"><stop offset="0" stop-color="#dba46c"/><stop offset="1" stop-color="#b97b40"/></radialGradient>
    <radialGradient id="pzg-fur-dark" cx="42%" cy="34%" r="72%"><stop offset="0" stop-color="#c2864a"/><stop offset="1" stop-color="#a66c35"/></radialGradient>
  </defs>
  <g class="pzg-bear">
    <g class="pzg-arm-rest">
      <rect x="12" y="83" width="34" height="14" rx="7" fill="url(#pzg-fur-dark)"/>
      <circle cx="15" cy="90" r="8.5" fill="url(#pzg-fur)"/>
    </g>
    <ellipse class="pzg-body" cx="60" cy="104" rx="31" ry="27" fill="url(#pzg-fur)"/>
    <ellipse cx="60" cy="108" rx="17" ry="15" fill="#efd3a8"/>
    <path class="pzg-scarf" d="M35 79 Q60 93 85 79 L85 88 Q60 102 35 88 Z" fill="var(--pz-accent, #5145cd)"/>
    <path class="pzg-scarf-tail" d="M72 88 L81 108 L67 103 Z" fill="var(--pz-accent, #5145cd)" opacity=".8"/>
    <g class="pzg-head">
      <circle cx="33" cy="31" r="12.5" fill="url(#pzg-fur-dark)"/><circle cx="33" cy="31" r="6" fill="#efd3a8"/>
      <circle cx="87" cy="31" r="12.5" fill="url(#pzg-fur-dark)"/><circle cx="87" cy="31" r="6" fill="#efd3a8"/>
      <circle cx="60" cy="52" r="31.5" fill="url(#pzg-fur)"/>
      <ellipse cx="60" cy="63.5" rx="15.5" ry="12" fill="#efd3a8"/>
      <ellipse cx="60" cy="58.5" rx="5.4" ry="3.9" fill="#3a2a1e"/>
      <path class="pzg-mouth" d="M60 62.4 V66 M54.5 66.8 Q60 71.5 65.5 66.8" stroke="#3a2a1e" stroke-width="1.9" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      <path class="pzg-mouth-big" d="M60 62.4 V65.5 M52.5 66 Q60 75 67.5 66 Z" stroke="#3a2a1e" stroke-width="1.9" fill="#7a3e3e" stroke-linecap="round" stroke-linejoin="round"/>
      <g class="pzg-eyes">
        <circle cx="48.5" cy="48" r="3.4" fill="#2b2118"/><circle cx="49.7" cy="46.7" r="1.15" fill="#fff"/>
        <circle cx="71.5" cy="48" r="3.4" fill="#2b2118"/><circle cx="72.7" cy="46.7" r="1.15" fill="#fff"/>
      </g>
      <ellipse cx="40.5" cy="59.5" rx="4.6" ry="2.6" fill="#e6968a" opacity=".5"/>
      <ellipse cx="79.5" cy="59.5" rx="4.6" ry="2.6" fill="#e6968a" opacity=".5"/>
    </g>
    <g class="pzg-arm">
      <rect x="80" y="81" width="27" height="14" rx="7" fill="url(#pzg-fur-dark)"/>
      <circle cx="106" cy="88" r="8.2" fill="url(#pzg-fur)"/>
      <rect class="pzg-finger" x="108" y="85.4" width="13" height="5.6" rx="2.8" fill="url(#pzg-fur)"/>
    </g>
  </g>
</svg>`;

let instances = 0;

/**
 * The avatar as a DOM node, ready to be appended.
 *
 * The gradient ids are made unique per copy: the guide puts three bears in one
 * root (the big one, the head in the help card, the chip that brings a hidden
 * guide back), and three elements sharing `id="pzg-fur"` is both invalid and
 * fragile — `url(#pzg-fur)` resolves to whichever came first, so removing that
 * one would drain the colour out of the others.
 */
export function createAvatar(): HTMLElement {
  const box = document.createElement('span');
  box.className = 'pz-guide-figure';
  instances += 1;
  box.innerHTML = GUIDE_AVATAR_SVG.replace(/pzg-fur/g, `pzg-fur-${instances}`);
  return box;
}

/**
 * The arm angle that points from the bear's shoulder at a target, in the
 * bear's own (unmirrored) frame, and whether the bear has to be mirrored to
 * reach it.
 *
 * `from` is the shoulder's position on screen and `to` the target. The arm's
 * zero is "straight right"; a target above yields a negative angle. A target on
 * the left is reached by mirroring the whole bear (so it still faces the
 * shopper) and pointing at the mirrored offset. The angle is clamped to what a
 * shoulder can do, and a target right under the bear gets the resting pose.
 */
export function aimAt(
  from: { x: number; y: number },
  to: { x: number; y: number },
): { angle: number; flipped: boolean } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.hypot(dx, dy);
  if (distance < 28) return { angle: REST_ANGLE, flipped: false };
  const flipped = dx < 0;
  const raw = (Math.atan2(dy, Math.abs(dx)) * 180) / Math.PI;
  const angle = Math.max(-100, Math.min(45, Math.round(raw)));
  return { angle, flipped };
}

/** The arm hanging loosely at the bear's side. */
export const REST_ANGLE = 28;
